import React, { useState, useRef, useEffect } from "react";
import { Form, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack } from "@mui/material";
import classes from "./AuthForm.module.scss";
import InputField from "../../../components/ui/Input/InputField";
import Button from "../../../components/ui/button/Button";
import {
    CognitoIdentityProviderClient,
    SignUpCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import authenticateUser from "../../../components/auth/AuthenticateUser";
import VerificationModal, {
    VerificationModalRef,
} from "../../../components/ui/verification-modal/VerificationModal";
import { resendVerificationCode } from "../../../utils/verificationUtils";
import { RootState, AppDispatch } from "../../../store/store";
import { selectFullProfile, selectProfileLoading } from "../../../store/slices/profileSlice";
import { selectIsAuthenticated } from "../../../store/slices/authSlice";

const STRINGS = {
    REGISTER_ACCOUNT: "Register account",
    EXPAND_NETWORK: "Sign up now to take advantage of more opportunities",
    FIRST_NAME: "First Name",
    FIRST_NAME_PLACEHOLDER: "Jane",
    LAST_NAME: "Last Name",
    LAST_NAME_PLACEHOLDER: "Doe",
    EMAIL: "Email",
    EMAIL_PLACEHOLDER: "jane@doe.com",
    PASSWORD: "Password",
    PASSWORD_PLACEHOLDER: "Enter password",
    CONFIRM_PASSWORD: "Confirm Password",
    CONFIRM_PASSWORD_PLACEHOLDER: "Re-enter password",
    SIGN_UP: "Sign Up",
    SIGN_UP_USING: "Sign up using",
    ALREADY_HAVE_ACCOUNT: "Already have an account?",
    LOGIN: "Login",
    FORM_ERRORS: {
        REQUIRED_FIELD: "This field is required",
        INVALID_EMAIL: "Please enter a valid email",
        PASSWORD_RULES: {
            LENGTH: "Password must be at least 8 characters long",
            LOWERCASE: "Password must contain at least one lowercase letter",
            UPPERCASE: "Password must contain at least one uppercase letter",
            NUMBER: "Password must contain at least one number",
            SPECIAL: "Password must contain at least one special character (@$!%*?&)",
        },
        PASSWORDS_DONT_MATCH: "Passwords do not match",
    },
    AUTH_ERRORS: {
        EMAIL_EXISTS: "This email is already registered. Please log in instead.",
        EMAIL_EXISTS_WRONG_PASSWORD:
            "An account with this email already exists. Please use a different email or try logging in.",
        UNEXPECTED_ERROR: "An unexpected error occurred. ☹️ Please try again.",
        VERIFICATION_FAILED: "Failed to send verification code. Please try again.",
        LOGIN_AFTER_VERIFICATION_FAILED:
            "Login failed after email verification. Please try logging in manually.",
    },
    FORM_TYPES: {
        LOGIN: "login",
    },
} as const;

interface SignupProps {
    onClickLogin: (form: string) => void;
}

const cognitoClient = new CognitoIdentityProviderClient({
    region: "ca-central-1",
});

// Regex for Email & Password
// eslint-disable-next-line no-useless-escape
const emailRegex = /^[A-Za-z0-9\._%+\-]+@[A-Za-z0-9\.\-]+\.[A-Za-z]{2,}$/;
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const Signup: React.FC<SignupProps> = ({ onClickLogin }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [formData, setFormData] = useState({
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
    });
    const [formSubmitted, setFormSubmitted] = useState(false);

    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState("");

    const navigate = useNavigate();

    const isAuthenticated = useSelector(selectIsAuthenticated);
    const fullProfile = useSelector(selectFullProfile);
    const isProfileLoading = useSelector(selectProfileLoading);

    const handelOnClickLogin = () => {
        onClickLogin(STRINGS.FORM_TYPES.LOGIN);
    };

    // Reference to the verification modal
    const verificationModalRef = useRef<VerificationModalRef>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value,
        });

        // Clear email error when user types
        if (id === "email") {
            setEmailError(false);
            setEmailErrorMessage("");
        }
    };

    const validateFormField = (
        fieldType: keyof typeof formData,
        checkFormSubmit: boolean,
    ): boolean => {
        if (checkFormSubmit && !formSubmitted) {
            return false; // Don't validate if form hasn't been submitted
        }

        switch (fieldType) {
            case "email":
                return !emailRegex.test(formData.email);
            case "password":
                return !passwordRegex.test(formData.password);
            case "confirmPassword":
                return formData.confirmPassword !== formData.password;
            case "firstName":
            case "lastName":
                return formData[fieldType].trim() === "";
            default:
                return false;
        }
    };

    const setErrorText = (fieldType: keyof typeof formData): string => {
        if (!formData[fieldType].length) {
            return STRINGS.FORM_ERRORS.REQUIRED_FIELD;
        } else {
            switch (fieldType) {
                case "email":
                    return emailRegex.test(formData.email) ? "" : STRINGS.FORM_ERRORS.INVALID_EMAIL;
                case "password": {
                    const rules = [
                        {
                            test: formData.password.length < 8,
                            message: STRINGS.FORM_ERRORS.PASSWORD_RULES.LENGTH,
                        },
                        {
                            test: !/[a-z]/.test(formData.password),
                            message: STRINGS.FORM_ERRORS.PASSWORD_RULES.LOWERCASE,
                        },
                        {
                            test: !/[A-Z]/.test(formData.password),
                            message: STRINGS.FORM_ERRORS.PASSWORD_RULES.UPPERCASE,
                        },
                        {
                            test: !/\d/.test(formData.password),
                            message: STRINGS.FORM_ERRORS.PASSWORD_RULES.NUMBER,
                        },
                        {
                            test: !/[@$!%*?&]/.test(formData.password),
                            message: STRINGS.FORM_ERRORS.PASSWORD_RULES.SPECIAL,
                        },
                    ];

                    const failedRule = rules.find((rule) => rule.test);
                    return failedRule ? failedRule.message : "";
                }
                case "confirmPassword":
                    return formData.confirmPassword !== formData.password
                        ? STRINGS.FORM_ERRORS.PASSWORDS_DONT_MATCH
                        : "";
                default:
                    return "";
            }
        }
    };

    const formIsDirty = (): boolean => {
        return Object.keys(formData).some((field) =>
            validateFormField(field as keyof typeof formData, false),
        );
    };

    // *** NEW: useEffect for handling navigation after signup/auth and profile fetch ***
    useEffect(() => {
        // After signup and authentication, always navigate to the initial profile setup page.
        // The setup page itself will handle redirecting to the newsfeed if the profile is already complete.
        if (isAuthenticated) {
            navigate("/profile-initial-setup");
        }
        // Dependency: run when auth state changes
    }, [isAuthenticated, navigate]);

    const handleVerificationSuccess = async () => {
        try {
            // After verification is successful, log the user in - IT NO LONGER NAVIGATES
            await authenticateUser(formData.email, formData.password, dispatch, navigate);
            // Navigation is handled by the useEffect hook above
        } catch (error) {
            // If the login fails after email verification, set an error message
            setEmailError(true);
            setEmailErrorMessage(STRINGS.AUTH_ERRORS.LOGIN_AFTER_VERIFICATION_FAILED);
        }
    };

    // Function to handle sending a verification code and showing the verification modal
    const handleSendVerificationAndShowModal = async () => {
        try {
            // Send verification code to user's email
            await resendVerificationCode(formData.email);

            // Open the verification modal
            verificationModalRef.current?.open();
        } catch (error) {
            setEmailError(true);
            setEmailErrorMessage(STRINGS.AUTH_ERRORS.VERIFICATION_FAILED);
        }
    };

    const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        setFormSubmitted(true);
        e.preventDefault(); // Prevent default form submission

        if (formIsDirty()) {
            return; // Don't proceed if there are errors in the form
        }

        try {
            // Handling SignUp with AWS Cognito
            const command = new SignUpCommand({
                ClientId: "5u806hjmmcee0qu442qddug8gv", // Replace with your actual User Pool Client ID
                Username: formData.email,
                Password: formData.password,
                UserAttributes: [
                    { Name: "email", Value: formData.email },
                    { Name: "given_name", Value: formData.firstName },
                    { Name: "family_name", Value: formData.lastName },
                ],
            });

            const response = await cognitoClient.send(command);

            // Check if the email was actually sent
            // if (response.UserConfirmed) {
            // } else {
            // }

            // Open the email verification modal
            verificationModalRef.current?.open();
        } catch (error: any) {
            if (error.name === "UsernameExistsException") {
                /*
                 * The user already exists, but their email might be unconfirmed. Check if
                 * their account is unconfirmed and handle accordingly.
                 */
                try {
                    // Try to authenticate the user - IT NO LONGER NAVIGATES
                    await authenticateUser(formData.email, formData.password, dispatch, navigate);
                    // If authentication succeeds, useEffect handles navigation
                    setEmailError(true);
                    setEmailErrorMessage(STRINGS.AUTH_ERRORS.EMAIL_EXISTS);
                } catch (authError: any) {
                    // If we get a UserNotConfirmedException, it means the user exists but their email is unconfirmed
                    if (authError.name === "UserNotConfirmedException") {
                        // Send verification code and show modal
                        await handleSendVerificationAndShowModal();
                    } else if (authError.name === "NotAuthorizedException") {
                        // Wrong password for existing account. User is trying to sign up with an email that already exists, but the password is incorrect. They can either go and log in from the login screen or sign up with a different email.
                        setEmailError(true);
                        setEmailErrorMessage(STRINGS.AUTH_ERRORS.EMAIL_EXISTS_WRONG_PASSWORD);
                    } else {
                        // Other authentication error
                        setEmailError(true);
                        setEmailErrorMessage(STRINGS.AUTH_ERRORS.UNEXPECTED_ERROR);
                    }
                }
            } else {
                setEmailError(true);
                setEmailErrorMessage(STRINGS.AUTH_ERRORS.UNEXPECTED_ERROR);
            }
        }
    };

    return (
        <>
            {/* Using the reusable verification modal for email verification */}
            <VerificationModal
                ref={verificationModalRef}
                email={formData.email}
                onVerificationSuccess={handleVerificationSuccess}
                onCancel={() => verificationModalRef.current?.close()}
            />

            {/* Welcome Text */}
            <Stack spacing={2} className={classes.authFormContainer}>
                <Box>
                    {/* Form */}
                    <Form className={classes.authForm}>
                        {/* Welcome Text */}
                        <div className={classes.welcomeText}>
                            <h4>{STRINGS.REGISTER_ACCOUNT}</h4>
                            <p>{STRINGS.EXPAND_NETWORK}</p>
                        </div>

                        {/* First Name */}
                        <InputField
                            label={STRINGS.FIRST_NAME}
                            name="firstName"
                            placeholder={STRINGS.FIRST_NAME_PLACEHOLDER}
                            type="text"
                            key="fname"
                            value={formData.firstName}
                            error={validateFormField("firstName", true)}
                            helperText={setErrorText("firstName")}
                            onChange={handleInputChange}
                        />

                        {/* Last Name */}
                        <InputField
                            label={STRINGS.LAST_NAME}
                            name="lastName"
                            placeholder={STRINGS.LAST_NAME_PLACEHOLDER}
                            type="text"
                            key="lname"
                            value={formData.lastName}
                            error={validateFormField("lastName", true)}
                            helperText={setErrorText("lastName")}
                            onChange={handleInputChange}
                        />

                        {/* Email */}
                        <InputField
                            label={STRINGS.EMAIL}
                            name="email"
                            placeholder={STRINGS.EMAIL_PLACEHOLDER}
                            type="email"
                            key="email"
                            value={formData.email}
                            error={emailError || validateFormField("email", true)}
                            helperText={emailError ? emailErrorMessage : setErrorText("email")}
                            onChange={handleInputChange}
                        />

                        {/* Password */}
                        <InputField
                            label={STRINGS.PASSWORD}
                            name="password"
                            placeholder={STRINGS.PASSWORD_PLACEHOLDER}
                            type="password"
                            key="password"
                            value={formData.password}
                            error={validateFormField("password", true)}
                            onChange={handleInputChange}
                            helperText={setErrorText("password")}
                        />

                        {/* Confirm Password */}
                        <InputField
                            onChange={handleInputChange}
                            label={STRINGS.CONFIRM_PASSWORD}
                            name="confirmPassword"
                            placeholder={STRINGS.CONFIRM_PASSWORD_PLACEHOLDER}
                            type="password"
                            key="confirm-password"
                            value={formData.confirmPassword}
                            error={validateFormField("confirmPassword", true)}
                            helperText={setErrorText("confirmPassword")}
                        />

                        <Stack spacing={2}>
                            <div>
                                <Button
                                    label={STRINGS.SIGN_UP}
                                    size="small"
                                    variant="primary"
                                    type="button"
                                    onClick={onSubmit}
                                />
                            </div>

                            {/* TODO: Uncomment the following "Sign Up With LinkedIn" once the Logging in with LinkedIn functionality works (again) */}
                            {/* <p className={classes.alternativeAuthWith}>{STRINGS.SIGN_UP_USING}</p>

                            <img
                                src={linkedinLogo}
                                alt="linkedin"
                                className={classes.linkedinLogo}
                            /> */}

                            <div className={classes.alternativeAuthLink}>
                                <p>
                                    {STRINGS.ALREADY_HAVE_ACCOUNT}{" "}
                                    <span onClick={handelOnClickLogin}>{STRINGS.LOGIN}</span>
                                </p>
                            </div>
                        </Stack>
                    </Form>
                </Box>
            </Stack>
        </>
    );
};

export default Signup;
